import { IInvalidField } from "shared";
import { useMemo, useState } from "react";
import { When } from "react-if";

import { ComboBox } from "../../../../../common/Atoms/ComboBox";
import { useAccountingResources } from "../../../../hooks/useAccountingResources";
import { FormDescription } from "../../../../../common/Atoms/FormDescription";
import { useDebounce } from "../../../../hooks/useDebounce";
import { classNames } from "../../../../../common/lib/classNames";

interface AccountSelectorProps {
  label?: string;
  value?: string | null;
  onChange: (e: { value: string; name: string }) => void;
  name: string;
  invalid?: IInvalidField;
  description?: string;
  types?: Array<`bank` | `income` | `expense` | `liability`>;
  helpIcon?: string;
  /** Is the value the account code or external account ID */
  valueKey?: `code` | `id`;
  /** Filter for this currency */
  onlyCurrency?: string;
  /** use ledger accounts instead of item accounts if the accounting system supplies any */
  useLedgerAccounts?: boolean;
  className?: string;
  clearable?: boolean;
}

export function AccountSelector({
  label,
  value,
  onChange,
  name,
  invalid,
  description,
  types = [`income`],
  valueKey = `code`,
  helpIcon,
  onlyCurrency,
  useLedgerAccounts = false,
  className,
  clearable,
}: AccountSelectorProps) {

  // More than this many options and we don't render them all in the dropdown
  // the user needs to type a search first to reduce the list of options
  const MAX_OPTIONS_FOR_NORMAL_MODE = 500;

  const { data: accountingResources, isLoading } = useAccountingResources();
  const [search, setSearch] = useState(``);

  const debouncedQuery = useDebounce(search, 500);

  const accounts = isLoading ? [] :
    // if we should use ledger accounts try to use those if they exist, otherwise use accounts
    // otherwise just use accounts
    // TODO: this feels a bit clunky - QBO needs sales items in invoices but ledger accounts elsewhere
    // maybe this should be a separate component.
    (useLedgerAccounts ? (accountingResources?.ledgerAccounts || accountingResources?.accounts || []) : (accountingResources?.accounts || []));

  const selected = useMemo(() => {
    const found = accounts.find(account => account[valueKey] === value);

    if (!found) return null;

    return {
      label: found.label,
      value: found[valueKey],
    };
  }, [accounts, value, valueKey]);

  const options = useMemo(() => {
    const opts = accounts.filter(account => {
      if (!types.includes(account.type)) return false;

      if (onlyCurrency && account.currencyCode.toUpperCase() !== onlyCurrency.toUpperCase()) return false;

      return (
        account.label.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
        account[valueKey].toLowerCase().includes(debouncedQuery.toLowerCase())
      );
    })
      .map(account => ({
        label: account.label,
        value: account[valueKey],
      }));

    if (clearable) {
      opts.unshift({
        label: `Use main policy income account`,
        value: null,
      });
    }

    return opts;
  }, [accounts, debouncedQuery, onlyCurrency, clearable]);

  function handleChange(e) {
    return onChange({
      value: e?.value,
      name,
    });
  }

  // Only render the text box until the options are low enough - i.e they have entered a search query that finds fewer options
  if (options && options.length > MAX_OPTIONS_FOR_NORMAL_MODE) {

    return (
      <div className={ classNames(
        `flex flex-col`,
        className,
      ) }>
        <ComboBox
          label={ label }
          options={ [] } // empty array is rendered until we have a more manageable list of results
          selected={ selected || null }
          query={ search }
          onQueryChange={ e => setSearch(e.target.value) }
          onSelect={ handleChange }
          invalid={ invalid }
          loading={ isLoading }
          placeholder={ `Search...` }
          helpIcon={ helpIcon }
          hideChevron={ true }
        />

        <When condition={ description }>
          <FormDescription>
            { description }
          </FormDescription>
        </When>
      </div>
    );
  }

  return (
    <div className={ classNames(
      `flex flex-col`,
      className,
    ) }>
      <ComboBox
        label={ label }
        options={ options }
        selected={ selected || null }
        query={ search }
        onQueryChange={ e => setSearch(e.target.value) }
        onSelect={ handleChange }
        invalid={ invalid }
        loading={ isLoading }
        placeholder={ `Search` }
        helpIcon={ helpIcon }
      />

      <When condition={ description }>
        <FormDescription>
          { description }
        </FormDescription>
      </When>
    </div>
  );
}
